'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import InlineSVG from '@src/components/Shared/InlineSVG';
import ProductBox from '@src/components/Shared/ProductBox';
import Caption from '@src/components/Shared/Caption/Caption';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import HeroValdProd from '@src/components/Sections/HeroValdProd';
import Testimonials from '@src/components/Sections/Testimonials';

import Profile from '@src/components/Icons/Profile';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { DynamoPageClientPropTypes } from './DynamoPage.types';

const DynamoPageClient: React.FC<DynamoPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/dynamo`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[3.2rem]! mb-[0rem]!'
        imgClassName='mt-[2rem] md:absolute md:right-[0] md:bottom-[-10rem] md:w-[58rem] lg:bottom-[0] lg:relative lg:w-[60rem] xl:left-[-15rem] xl:w-[70rem] xl:left-[0rem] 2xl:w-[70rem] 2xl:left-[-10rem] 3xl:left-[-20rem] 2xl:bottom-[0] xsm:min-h-[34.8297rem] md:min-h-[58rem] lg:min-h-[60rem] xl:min-h-[70rem]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] xsm:min-h-[7.9828rem] md:min-h-[7.4234rem] lg:min-h-[10.3797rem]'
        introClassName='tracking-[-0.0342rem] leading-[2.7rem] text-[1.8rem] max-w-[30.5rem] md:text-[2rem] md:leading-[3rem] md:tracking-[-0.036rem] md:max-w-[39.6rem] lg:max-w-[48.5rem] lg:text-[2.4rem] lg:leading-[3.6rem]'
        headerClassName='md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%] lg:mt-[-20rem]'
      />

      <ImageTextSecondary
        id={page?.hundredsOfTests?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.hundredsOfTests?.items?.[0]?.title}
        titleClassName='2xl:locale-es:max-w-[67rem]'
        imgURL={page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.url}
        priority={true}
        imgFirstRatio={
          page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.width /
          page?.hundredsOfTests?.items?.[0]?.mediaPrimary?.height
        }
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem] [&>p>a]:drop-shadow-xs'
              bodyCopy={page?.hundredsOfTests?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.hundredsOfTests?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.modularity?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible! py-[6rem] lg:py-[9rem]'
        pretitle={page?.modularity?.items?.[0]?.preTitle}
        title={page?.modularity?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs 2xl:locale-es:max-w-[63rem]'
        imgURL={page?.modularity?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.modularity?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        id={page?.engagement?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.engagement?.items?.[0]?.preTitle}
        title={page?.engagement?.items?.[0]?.title}
        imgURL={page?.engagement?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='pb-[0.5rem] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.engagement?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.useability?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! py-[0]!'
        pretitle={page?.useability?.items?.[0]?.preTitle}
        title={page?.useability?.items?.[0]?.title}
        imgURL={page?.useability?.items?.[0]?.mediaPrimary?.url}
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.useability?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container
        className='overflow-visible! lg:mb-[12.2rem]!'
        id={page?.efficiency?.items?.[0]?.scrollTo?.slice(1)}
      >
        <SectionHeader
          className='mb-[4rem]'
          pretitle={page?.efficiency?.items?.[0]?.preTitle}
          title={page?.efficiency?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[1.3] xl:leading-[5rem] font-light 2xl:max-w-[58.8rem] '
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.efficiency?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        </SectionHeader>

        <div className='flex flex-col overflow-visible! lg:flex-row'>
          <InlineSVG
            className='mb-[2.4rem] lg:order-1 lg:mb-[unset] lg:hidden'
            src={page?.efficiency?.items?.[0]?.extraAssetsCollection?.items?.[1]?.media?.url}
            renderAt={['mobile', 'tablet']}
          />

          <InlineSVG
            className='mb-[2.4rem] hidden w-full! lg:order-1 lg:mb-[-1rem] lg:block'
            src={page?.efficiency?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
            renderAt={['desktop']}
          />

          <div className='relative -order-1 mb-[3.4rem] ml-[-2.4rem] flex max-w-[90%] items-center gap-[1.6rem] overflow-visible! rounded-br-[100rem] rounded-tr-[100rem] bg-background-tertiary py-[2.2rem] pl-[2.4rem] pr-[5.2rem] md:ml-[-6.3rem] md:pl-[6.3rem] lg:mb-[0] lg:max-w-full lg:self-center lg:py-[0] lg:pr-[0]'>
            <Profile className='h-[4rem] w-[4rem] text-body-copy lg:h-[5.4rem] lg:w-[5.4rem]' />
            <p className='whitespace-nowrap text-[1.6rem] font-normal leading-[2rem] lg:text-[2rem]'>
              <span className='font-medium text-body-copy'>Sarah's Upper Body</span> <br />{' '}
              <span className='font-medium text-body-copy'>Function</span> Test Battery
            </p>

            <div className='hidden lg:absolute lg:left-[0] lg:z-[-1] lg:ml-[-100vw] lg:block lg:h-[190%] lg:w-[200vw] lg:bg-background-tertiary' />
          </div>
        </div>
        <Caption
          bodyCopy={page?.efficiency?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy}
        />
      </Container>

      <ImageTextSecondary
        id={page?.visibility?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible! pb-[0]! pt-[6rem]'
        pretitle={page?.visibility?.items?.[0]?.preTitle}
        title={page?.visibility?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.visibility?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem] [&>p>a]:drop-shadow-xs'
              bodyCopy={page?.visibility?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.visibility?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container id={page?.versatility?.items?.[0]?.scrollTo?.slice(1)} className='mb-[0]!'>
        <SectionHeader
          className='justify-center lg:pb-[unset]'
          pretitle={page?.versatility?.items?.[0]?.preTitle}
          title={page?.versatility?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] text-[2.8rem] xl:text-[4.8rem] leading-[3.2rem] xl:leading-[5rem] font-light'
          align='center'
        />

        <div className='pb-[1rem] pt-[4.2rem] md:py-[8rem] lg:mt-[-10rem]'>
          <ProductBox
            className='mb-[5.2rem] text-left! sm:text-center md:max-w-[46rem]! md:locale-es:max-w-[60rem]! md:locale-fr:max-w-[60rem]! lg:relative lg:bottom-[-10rem] md:[&>div>p]:max-w-[46rem]'
            buttonClassName='justify-start! sm:gap-[1rem]'
            product={page?.dynamoPlus?.items?.[0]}
          />

          <InlineSVG
            className='mb-[2.4rem] w-full md:hidden lg:order-1 lg:mb-[unset]'
            src={page?.dynamoPlus?.items?.[0]?.mediaTertiary?.url}
            renderAt={['mobile']}
          />

          <InlineSVG
            className='mb-[2.4rem] hidden md:order-1 md:mb-[-1rem] md:block md:w-full lg:hidden'
            src={page?.dynamoPlus?.items?.[0]?.mediaSecondary?.url}
            renderAt={['tablet']}
          />

          <InlineSVG
            className='hidden lg:order-1 lg:block'
            src={page?.dynamoPlus?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop']}
          />
        </div>
      </Container>

      <Container
        id={page?.dynamoLight?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='mb-[0rem]!'
      >
        <div className='pb-[0.5rem] pt-[3.2rem] md:py-[8rem]'>
          <ProductBox
            className='mb-[5.2rem] text-left! sm:text-center md:max-w-[46rem]! md:bg-background-secondary! md:locale-es:max-w-[60rem]! md:locale-fr:max-w-[52rem]! lg:relative lg:bottom-[-10rem] lg:ml-auto md:[&>div>p]:max-w-[46rem]'
            buttonClassName='justify-start! sm:gap-[1rem]'
            product={page?.dynamoLight?.items?.[0]}
          />

          <InlineSVG
            className='mb-[2.4rem] w-full lg:order-1 lg:mb-[unset] lg:hidden'
            src={page?.dynamoLight?.items?.[0]?.mediaSecondary?.url}
            renderAt={['mobile', 'tablet']}
          />

          <InlineSVG
            className='mb-[2.4rem] hidden md:order-1 md:mb-[-1rem] md:w-full lg:block'
            src={page?.dynamoLight?.items?.[0]?.mediaPrimary?.url}
            renderAt={['desktop']}
          />
        </div>
      </Container>

      <Container
        id={page?.dynamoMax?.items?.[0]?.scrollTo?.slice(1)}
        className='px-[0]! pb-[2.4rem]! before:absolute before:block before:h-[466px] before:w-full before:bg-[#25282A] before:content-[""] xsm:before:h-[440px] sm:before:h-[285px] md:before:h-[250px] lg:px-[6.4rem]! lg:pt-[7.2rem] lg:before:hidden xl:px-[12.8rem]! 3xl:px-[4rem]!'
        background='bg-[#181818]'
      >
        <ProductBox
          className='pb-[4rem] pt-[2.4rem] text-left! md:rounded-none! md:border-0  md:bg-[unset]! md:px-[0] md:locale-es:max-w-[60rem]! md:locale-fr:max-w-[53rem]! lg:relative lg:z-15 lg:mt-[-1.6rem] lg:max-w-[46rem]  lg:rounded-[2.4rem]! lg:bg-[#25282A]! lg:text-center 2xl:static! 2xl:z-[unset] md:[&>div>p]:max-w-[46rem]'
          buttonClassName='px-[2.4rem] [&>div>a]:relative [&>div>a]:z-20 justify-start! px-[2.4rem] sm:gap-[1rem] sm:[&>div]:w-[unset]'
          headingClassName='text-[4rem] text-[#fff] mb-[0] px-[2.4rem]'
          bodyCopyClassName='text-[#C3C3C3] text-[2rem] leading-[3rem] px-[2.4rem]'
          product={page?.dynamoMax?.items?.[0]}
          announcement={page?.dynamoMax?.items?.[0]?.preTitle}
        />

        <InlineSVG
          className='xl:max-auto relative z-10 hidden w-full lg:mb-[-8rem] lg:mt-[-25rem] lg:block xl:mb-[-9rem] xl:ml-[10rem] xl:max-w-[117rem] 3xl:mb-[-11.5rem] 3xl:ml-[0] 3xl:max-w-[unset]'
          src={page?.dynamoMax?.items?.[0]?.mediaPrimary?.url}
          renderAt={['desktop']}
        />
        <InlineSVG
          className='relative z-[unset] hidden pb-[2rem] md:mb-[-4rem] md:mt-[-11rem] md:block md:w-full lg:hidden'
          src={page?.dynamoMax?.items?.[0]?.mediaSecondary?.url}
          renderAt={['tablet']}
        />
        <InlineSVG
          className='relative z-[unset] mb-[-2.3rem] mt-[-4rem] md:mb-[-1rem] md:hidden md:w-full'
          src={page?.dynamoMax?.items?.[0]?.mediaTertiary?.url}
          renderAt={['mobile']}
        />
      </Container>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mb-[0]! pb-[0] pt-[8rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          className='[&>div>div>ul>li:first-child:has(>div>span)>div>div]:max-w-sm md:[&>div>div>ul>li:first-child:has(>div>span)>div>div]:max-w-[27rem] [&>div>div>ul]:gap-[0]'
          headerImgL='w-[11rem] lg:w-[13rem] lg:mt-[-6rem]'
          headerImgR='w-[8rem] lg:w-[10rem] lg:mt-[-4rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts
        data={page?.valdSystems?.items?.[0]}
        background='bg-background-tertiary'
        className='py-[8rem]'
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default DynamoPageClient;
